import { inject, Injectable } from '@angular/core';

import { Workbook } from 'exceljs';
import saveAs from 'file-saver';

import { ToPlainText } from '@empathy/global/functions';
import { Translation } from '@empathy/survey-core';

import { SurveyEditorService } from './survey-editor.service';
import { TranslationExportOptions } from '../models/translations/translation-export-options.interface';
import { questionKeyRegex } from '../models/translations/translation-key.constant';
import { TranslationColumns, TranslationColumnWidths } from '../models/translations/translations-import-export.contants';

@Injectable({ providedIn: 'root' })
export class ExportTranslationsService {
  private surveyEditorService = inject(SurveyEditorService);

  export(surveyTitle: string, defaultTranslation: Translation, localizedTranslation: Translation, exportOptions: TranslationExportOptions): void {
    const workbook = new Workbook();

    const title = ToPlainText(surveyTitle);
    const fileName = `${title}-${localizedTranslation.code}`;

    const worksheet = workbook.addWorksheet(localizedTranslation.code);
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];

    const headerRow = worksheet.addRow(['ID', 'Ignore', defaultTranslation.code, localizedTranslation.code]);

    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
      cell.font = { bold: true };
      cell.protection = { locked: true };
    });

    const defaultMap = new Map(defaultTranslation.entries.map((e) => [e.key, e.value]));
    const localizedMap = new Map(localizedTranslation.entries.map((e) => [e.key, e.value]));
    const ignoredMap = new Map(localizedTranslation.entries.map((e) => [e.key, e.ignoredInd ?? false]));

    const keys = this.buildExportKeys(defaultTranslation, localizedTranslation, exportOptions);

    for (const key of keys) {
      const id = this.toExportId(key);

      const row = worksheet.addRow([id, ignoredMap.get(key) ?? false, defaultMap.get(key) ?? '', localizedMap.get(key) ?? '']);

      row.eachCell((cell) => (cell.protection = { locked: true }));
      row.getCell(TranslationColumns.ignore).protection = { locked: false };
      row.getCell(TranslationColumns.localizedLanguage).protection = { locked: false };
    }

    worksheet.getColumn(TranslationColumns.id).width = TranslationColumnWidths.id;
    worksheet.getColumn(TranslationColumns.ignore).width = TranslationColumnWidths.ignore;
    worksheet.getColumn(TranslationColumns.defaultLanguage).width = TranslationColumnWidths.defaultLanguage;
    worksheet.getColumn(TranslationColumns.localizedLanguage).width = TranslationColumnWidths.localizedLanguage;

    [TranslationColumns.defaultLanguage, TranslationColumns.localizedLanguage].forEach((col) => {
      worksheet.getColumn(col).alignment = { wrapText: true, vertical: 'top' };
    });

    const firstDataRow = 2;
    const lastDataRow = worksheet.lastRow?.number ?? firstDataRow - 1;

    for (let r = firstDataRow; r <= lastDataRow; r++) {
      worksheet.getCell(r, TranslationColumns.ignore).dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: ['"TRUE,FALSE"'],
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'Invalid value',
        error: 'Only TRUE or FALSE is allowed.'
      };
    }

    worksheet.protect('', { selectLockedCells: true, selectUnlockedCells: true });

    this.saveWorkbook(workbook, fileName);
  }

  private buildExportKeys(defaultTranslation: Translation, localizedTranslation: Translation, options: TranslationExportOptions): string[] {
    if (options.all) {
      return localizedTranslation.entries.map((e) => e.key);
    }

    const showMissing = options.missing;
    const showOutdated = options.outdated;
    const showIgnored = options.ignored;
    const defaultMap = new Map(defaultTranslation.entries.map((entry) => [entry.key, entry.value]));

    return localizedTranslation.entries
      .filter((entry) => {
        const isIgnored = !!entry.ignoredInd;
        const isOutdated = !!entry.outdatedInd;
        const sourceValue = (defaultMap.get(entry.key) ?? '').trim();
        const isMissing = sourceValue.length > 0 && !entry.value?.trim() && !isIgnored;

        return (showMissing && isMissing) || (showOutdated && isOutdated) || (showIgnored && isIgnored);
      })
      .map((e) => e.key);
  }

  private saveWorkbook(workbook: Workbook, fileName: string): void {
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);
    });
  }

  private toQuestionCode(questionId: string): string | undefined {
    const question = this.surveyEditorService.surveyEditor$().questions.find((q) => q.id === questionId);

    if (question) {
      return question.code;
    }
  }

  private toExportId(key: string): string {
    if (!questionKeyRegex.test(key)) {
      return key;
    }

    const questionId = key.split('.')[0];
    const questionCode = this.toQuestionCode(questionId);

    return questionCode ? key.replace(questionId, questionCode) : key;
  }
}
